import { useMemo } from 'react';

import { q } from '@actual-app/core/shared/query';
import { useQuery } from '@tanstack/react-query';

import {
  cuentasSinCuadrar,
  idsSinCuadreNunca,
} from '#components/mobile/inicio/sinCuadrar';
import { useCuentasEspeciales } from '#components/mobile/inicio/widgets/useCuentasEspeciales';
import { useAccounts } from '#hooks/useAccounts';
import { aqlQuery } from '#queries/aqlQuery';

/**
 * Cuentas con más de un mes sin cuadrar, para «Por hacer» de Inicio. El
 * último cuadre es `accounts.last_reconciled` (lo guarda Actual al
 * conciliar); a las que nunca se han cuadrado se les busca el primer
 * movimiento, una consulta por cuenta (suelen ser pocas).
 */
export function useCuentasSinCuadrar() {
  const { data: cuentas = [] } = useAccounts();
  const { prestamos, cargando } = useCuentasEspeciales();
  const idsPrestamo = useMemo(
    () => new Set(prestamos.map(p => p.account.id)),
    [prestamos],
  );
  const nunca = useMemo(
    () => (cargando ? [] : idsSinCuadreNunca(cuentas, idsPrestamo)),
    [cargando, cuentas, idsPrestamo],
  );

  const { data: primeros } = useQuery({
    queryKey: ['cuentas', 'primer-movimiento', nunca],
    enabled: nunca.length > 0,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const filas = await Promise.all(
        nunca.map(async id => {
          const { data } = await aqlQuery(
            q('transactions')
              .filter({ account: id })
              .options({ splits: 'none' })
              .orderBy({ date: 'asc' })
              .limit(1)
              .select(['date']),
          );
          const fecha = (data as { date?: string }[])[0]?.date;
          return fecha ? ([id, fecha] as const) : null;
        }),
      );
      return new Map(filas.filter(f => f != null));
    },
  });

  return useMemo(() => {
    if (cargando) {
      return [];
    }
    return cuentasSinCuadrar(
      cuentas,
      idsPrestamo,
      primeros ?? new Map(),
      Date.now(),
    );
  }, [cargando, cuentas, idsPrestamo, primeros]);
}
